/* eslint-disable eqeqeq */
import { handleTabToNext, cmsList } from '../../formKit';
import { ACCESSORY_FORM_CONFIGS } from '../accessoryFormConfigs';
import { useAccessoryForm } from '../useAccessoryForm';
import { ABareInput, AccSubtab } from '../accessoryFieldKit';
import MountingSection from '../MountingSection';
import AccessoryMountingPopup, {
  CasingMountSpecialParameters,
} from '../AccessoryMountingPopup';
import type { AccessoryFormProps } from '../AccessoryFormHost';

/**
 * Phase 6e2b-1 — DOG HOUSE form (accessories/dog_house/{form.js 70,
 * form.tmpl 299}, read in full). No catalogue fields; properties = W×H×D
 * triple + Base checkbox + casing/floor rows read from the BOARD cms doc
 * (cms.casing.* / cms.floor.* — NOT cms_accessory); floor_mtl_gauge_id null
 * → cascades floor_under_insulation_id/floor_under_liner_mtl_gauge_id null
 * (chassis commitModelField special) + the two dependent selects gate
 * non-clickable on floor_mtl_gauge_id; mounting = dogHouse variant
 * (wall_left/wall_right only), sublist gated on disableMounting (w&h&depth
 * set — §49.6:741); remarks input carries data-subtab="performance" (legacy
 * copy-paste quirk — state tracked under 'remarks' so the toggle works;
 * deviation note).
 */
const config = ACCESSORY_FORM_CONFIGS.dog_house;

const DogHouseForm = ({ row, section, tunnel, cms, expanded }: AccessoryFormProps) => {
  const api = useAccessoryForm({
    config,
    row,
    section,
    tunnel,
    expanded,
    labeledSubtabs: ['mounting', 'remarks'],
  });
  const { model } = api;
  if (api.loading || !model) {
    return <div className="preloader dotted-preloader active" />;
  }
  const cid = row.id;
  const cmsDoc = cms as Record<string, unknown> | undefined;
  const disableMounting =
    model.get('width') !== null && model.get('height') !== null && model.get('depth') !== null;
  const floorGaugeSet = !!model.get('floor_mtl_gauge_id');
  const metalLabel = (o: Record<string, unknown>) =>
    o.label != null ? String(o.label) : String(o.model ?? '');

  const casingSelect = (
    name: string,
    label: string,
    path: string,
    opts?: { spn?: boolean; nonClickable?: boolean; tabIndexMinus?: boolean }
  ) => {
    const list = cmsList(cmsDoc, path);
    const current = list.find((o) => o.id == model.get(name));
    return (
      <li>
        <div className="single-line">
          <span className="selection">{current ? <span>{opts?.spn ? String(current.spn ?? '') : metalLabel(current)}</span> : null}</span>
          <select
            name={name}
            required
            className={`model_field${opts?.nonClickable && !floorGaugeSet ? ' non-clickable' : ''}`}
            tabIndex={opts?.tabIndexMinus ? -1 : undefined}
            value={model.get(name) != null ? String(model.get(name)) : 'None'}
            onChange={(e) => api.commitModelField(name, e.target.value)}
          >
            <option value="None">- -</option>
            {list.map((o) => (
              <option key={String(o.id)} value={String(o.id)}>
                {opts?.spn ? String(o.spn ?? '') : metalLabel(o)}
              </option>
            ))}
          </select>
          <span className="label">{label}</span>
        </div>
      </li>
    );
  };

  return (
    <>
      <form ref={(el) => { api.formRef.current = el; }} onKeyDown={handleTabToNext}>
        <ul className="inputs-group sublist dog_house">
          <AccSubtab api={api} type="dog_house" cid={cid} name="properties" sublistClass="max-height-auto">
            <li>
              <div className="multiply">
                {/* §JSX-WS — the five `{' '}`s are load-bearing. `.unit` is
                    inline-block and the three `.small-input`s and two
                    `.multiply-sign`s are inline, so every NEWLINE the tmpl
                    leaves between them (form.tmpl:9-24 — the `<!-- invalid-tooltip -->`
                    comments do not break the whitespace run) renders as a real
                    space. JSX drops whitespace containing a newline, so the row
                    sat progressively left of legacy. No space before the
                    floated `.label` (`>.label { float: left }`) — legacy has a
                    newline there too and it does not render. */}
                <span className="unit">in</span>{' '}
                <ABareInput api={api} name="width" fieldClass="model_field small-input" min={12} max={600} />{' '}
                <span className="multiply-sign">x</span>{' '}
                <ABareInput api={api} name="height" fieldClass="model_field small-input" min={12} max={160} />{' '}
                <span className="multiply-sign">x</span>{' '}
                <ABareInput api={api} name="depth" fieldClass="model_field small-input" min={12} max={288} />
                <span className="label">
                  <span className="required">Width</span> x <span className="required">Height</span> x{' '}
                  <span className="required">Depth</span>
                </span>
              </div>
            </li>
            <li>
              <div className="single-line">
                <input
                  type="checkbox"
                  name="base_mtl_and_height"
                  className="model_field"
                  style={{ pointerEvents: 'all' }}
                  checked={!!model.get('base_mtl_and_height')}
                  onChange={(e) => api.commitModelCheckbox('base_mtl_and_height', e.target.checked)}
                />
                <span className="label">Base</span>
              </div>
            </li>
            {casingSelect('casing_outer_wall_mtl_gauge_id', 'Casing Outer Skin', 'casing.metal_sheets')}
            {casingSelect('casing_outer_wall_outer_finish_id', 'Casing Skin Finish', 'casing.finishes', { spn: true })}
            {casingSelect('casing_insulation_id', 'Casing Insulation', 'casing.insulations')}
            {casingSelect('casing_inner_liner_mtl_gauge_id', 'Casing Inner Liner', 'casing.liner_metal_sheets', { tabIndexMinus: true })}
            {casingSelect('floor_mtl_gauge_id', 'Floor Deck', 'floor.metal_sheets', { tabIndexMinus: true })}
            {casingSelect('floor_finish_id', 'Deck Finish', 'floor.finishes', { spn: true })}
            {casingSelect('floor_under_insulation_id', 'Floor Under-Insulation', 'floor.under_insulations', { nonClickable: true })}
            {casingSelect('floor_under_liner_mtl_gauge_id', 'Floor Under-Liner', 'floor.under_liner_metal_sheets', { nonClickable: true })}
          </AccSubtab>

          <MountingSection
            api={api}
            type="dog_house"
            cid={cid}
            variant="dogHouse"
            liClass=""
            sublistDisabled={!disableMounting}
          />

          {/* remarks — legacy input id `dog_house{{cid}}_remarks` (no
              underscore) + data-subtab="performance" quirk. */}
          <AccSubtab
            api={api}
            type="dog_house"
            cid={cid}
            name="performance"
            stateKey="remarks"
            inputId={`dog_house${cid}_remarks`}
            label="Remarks"
            liClass="group-with-include-checkbox"
            labelExtra={<span className="short_remarks">{String(model.get('remarks') ?? '')}</span>}
          >
            <li className="each-row">
              <input
                type="text"
                autoComplete="off"
                placeholder="300 characters maximum"
                maxLength={300}
                className="textarea model_field"
                name="remarks"
                defaultValue={String(model.get('remarks') ?? '')}
                onBlur={(e) => api.commitModelField('remarks', e.target.value)}
              />
            </li>
          </AccSubtab>
        </ul>
      </form>
      <AccessoryMountingPopup
        api={api}
        typeName="dog house"
        typeValue="dog_house"
        SpecialParameters={CasingMountSpecialParameters}
      />
    </>
  );
};

export default DogHouseForm;
